import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Tractor, Calculator, Plant, FileText } from 'lucide-react';
import Header from '../../../components/layout/Header';
import Footer from '../../../components/layout/Footer';
import Breadcrumbs from '../../../components/ui/Breadcrumbs';
import ToolSidebar from '../../../components/layout/ToolSidebar';
import { useTheme } from '../../../contexts/ThemeContext';

// Импорт компонентов калькулятора
import CalculationForm from './AgriculturalSubsidiesCalculatorPage/components/CalculationForm';
import ResultsOverview from './AgriculturalSubsidiesCalculatorPage/components/ResultsOverview';
import SubsidySchedule from './AgriculturalSubsidiesCalculatorPage/components/SubsidySchedule';
import QuickAdjustmentSidebar from './AgriculturalSubsidiesCalculatorPage/components/QuickAdjustmentSidebar';
import ComparisonSidebar from './AgriculturalSubsidiesCalculatorPage/components/ComparisonSidebar';
import TipsSidebar from './AgriculturalSubsidiesCalculatorPage/components/TipsSidebar';
import InfoContent from './AgriculturalSubsidiesCalculatorPage/components/InfoContent';

// Определение начального состояния формы
export const initialFormData = {
  farmSize: '100', // Размер фермерского хозяйства в гектарах
  farmType: 'crop', // Тип хозяйства: crop (растениеводство), livestock (животноводство), mixed (смешанное)
  regionCode: '77', // Код региона (например, 77 для Москвы)
  organizationType: 'individual', // individual (ИП/КФХ), legal (Юр.лицо)
  farmingExperience: '3', // Опыт в сельском хозяйстве (в годах)
  annualRevenue: '5000000', // Годовая выручка в рублях
  employeeCount: '5', // Количество сотрудников
  
  // Критерии для программ субсидирования
  isEcoFriendly: false, // Экологически чистое производство
  isInnovative: false, // Инновационные технологии
  isExporter: false, // Экспортная деятельность
  hasInsurance: false, // Наличие страхования
  hasSocialPrograms: false, // Участие в социальных программах
  
  // Выбранные программы субсидирования
  includeCapitalGrants: true, // Гранты на капитальные затраты
  capitalGrantsAmount: '1000000', // Размер запрашиваемой субсидии
  capitalGrantsPurpose: 'equipment', // Цель: equipment, construction, land
  
  includeOperationalSubsidies: false, // Субсидии на операционные расходы
  operationalSubsidiesAmount: '500000', // Размер запрашиваемой субсидии
  operationalSubsidiesType: 'seeds', // Тип: seeds, fertilizers, fuel
  
  includeLoanSubsidies: false, // Субсидирование процентной ставки по кредитам
  loanAmount: '3000000', // Сумма кредита
  loanTerm: '5', // Срок кредита в годах
  loanRate: '12', // Процентная ставка
  
  includeExportSupport: false, // Поддержка экспорта
  exportRevenue: '2000000', // Выручка от экспорта
  exportCountries: 'china,belarus,kazakhstan', // Страны экспорта
  
  // Период программы
  startDate: new Date().toISOString().split('T')[0], // Дата начала
  programDuration: '3', // Продолжительность в годах
};

// Определение типов фермерских хозяйств
export const farmTypes = [
  { id: 'crop', name: 'Растениеводство', defaultEmployeeCount: 5 },
  { id: 'livestock', name: 'Животноводство', defaultEmployeeCount: 8 },
  { id: 'mixed', name: 'Смешанное хозяйство', defaultEmployeeCount: 10 },
  { id: 'poultry', name: 'Птицеводство', defaultEmployeeCount: 7 },
  { id: 'dairy', name: 'Молочное животноводство', defaultEmployeeCount: 9 },
  { id: 'organic', name: 'Органическое земледелие', defaultEmployeeCount: 6 }
];

// Определение типов организаций
export const organizationTypes = [
  { id: 'individual', name: 'КФХ / ИП', maxSubsidyPercent: 90 },
  { id: 'legal', name: 'ООО / АО', maxSubsidyPercent: 70 },
  { id: 'cooperative', name: 'Сельхозкооператив', maxSubsidyPercent: 85 },
  { id: 'agro_holding', name: 'Агрохолдинг', maxSubsidyPercent: 50 }
];

// Определение регионов
export const regions = [
  { code: '77', name: 'Москва', subsidyCoefficient: 0.8 },
  { code: '78', name: 'Санкт-Петербург', subsidyCoefficient: 0.8 },
  { code: '50', name: 'Московская область', subsidyCoefficient: 0.9 },
  { code: '23', name: 'Краснодарский край', subsidyCoefficient: 1.2 },
  { code: '61', name: 'Ростовская область', subsidyCoefficient: 1.15 },
  { code: '31', name: 'Белгородская область', subsidyCoefficient: 1.1 },
  { code: '36', name: 'Воронежская область', subsidyCoefficient: 1.05 },
  { code: '02', name: 'Республика Башкортостан', subsidyCoefficient: 1.0 },
  { code: '16', name: 'Республика Татарстан', subsidyCoefficient: 1.0 },
  { code: '74', name: 'Челябинская область', subsidyCoefficient: 0.95 },
  { code: '55', name: 'Омская область', subsidyCoefficient: 1.0 },
  { code: '24', name: 'Красноярский край', subsidyCoefficient: 1.05 },
  { code: '38', name: 'Иркутская область', subsidyCoefficient: 1.1 },
  { code: '25', name: 'Приморский край', subsidyCoefficient: 1.15 },
  { code: '14', name: 'Республика Саха (Якутия)', subsidyCoefficient: 1.3 },
  { code: '00', name: 'Другой регион', subsidyCoefficient: 1.0 }
];

// Определение целей капитального гранта
export const capitalGrantPurposes = [
  { id: 'equipment', name: 'Приобретение оборудования', fundingPercent: 60 },
  { id: 'construction', name: 'Строительство объектов', fundingPercent: 50 },
  { id: 'land', name: 'Приобретение земли', fundingPercent: 40 },
  { id: 'livestock', name: 'Закупка племенного скота', fundingPercent: 70 },
  { id: 'processing', name: 'Оборудование для переработки', fundingPercent: 65 },
  { id: 'storage', name: 'Создание хранилищ', fundingPercent: 55 }
];

// Определение типов операционных субсидий
export const operationalSubsidyTypes = [
  { id: 'seeds', name: 'Семена и посадочный материал', fundingPercent: 50 },
  { id: 'fertilizers', name: 'Удобрения и СЗР', fundingPercent: 45 },
  { id: 'fuel', name: 'Топливо и ГСМ', fundingPercent: 30 },
  { id: 'salaries', name: 'Заработная плата', fundingPercent: 25 },
  { id: 'insurance', name: 'Страхование посевов/животных', fundingPercent: 60 },
  { id: 'utilities', name: 'Коммунальные услуги', fundingPercent: 40 }
];

/**
 * AgriculturalSubsidiesCalculatorPage Component - Полный калькулятор сельскохозяйственных субсидий
 * с различными типами программ и параметрами.
 */
const AgriculturalSubsidiesCalculatorPage = () => {
  const { subToolId } = useParams();
  const { darkMode, toggleDarkMode } = useTheme();

  // --- Данные инструмента ---
  const pageTitle = 'Калькулятор сельскохозяйственных субсидий | Gensio';
  const metaDescription = 'Полный калькулятор сельскохозяйственных субсидий: гранты на развитие, компенсация капитальных затрат, льготное кредитование, субсидирование процентных ставок и другие формы государственной поддержки для фермеров и сельхозпроизводителей.';

  // --- Управление состоянием ---
  const [formData, setFormData] = useState(initialFormData);
  const [results, setResults] = useState(null);
  const [schedule, setSchedule] = useState([]);
  const [showFullSchedule, setShowFullSchedule] = useState(false);
  const [errors, setErrors] = useState({});
  const [isCalculating, setIsCalculating] = useState(false);
  const [savedCalculations, setSavedCalculations] = useState([]);

  // --- Обновление заголовка и мета-описания ---
  useEffect(() => {
    document.title = pageTitle;
    let metaTag = document.querySelector('meta[name="description"]');
    if (!metaTag) {
      metaTag = document.createElement('meta');
      metaTag.name = 'description';
      document.head.appendChild(metaTag);
    }
    metaTag.setAttribute('content', metaDescription);
  }, [pageTitle, metaDescription]);

  // --- Загрузка сохраненных расчетов из localStorage ---
  useEffect(() => {
    const savedCalculationsData = localStorage.getItem('savedAgriculturalSubsidies');
    if (savedCalculationsData) {
      try {
        setSavedCalculations(JSON.parse(savedCalculationsData));
      } catch (e) {
        console.error("Error loading saved calculations:", e);
      }
    }
  }, []);

  // --- Обработка subToolId для предварительной настройки формы ---
  useEffect(() => {
    if (!subToolId) return;

    setFormData(prev => {
      let newState = { ...initialFormData };

      switch (subToolId) {
        case 'grants':
          newState.includeCapitalGrants = true;
          newState.includeOperationalSubsidies = false;
          newState.includeLoanSubsidies = false;
          newState.includeExportSupport = false;
          break;
        case 'operational':
          newState.includeCapitalGrants = false;
          newState.includeOperationalSubsidies = true;
          newState.includeLoanSubsidies = false;
          newState.includeExportSupport = false;
          break;
        case 'loans':
          newState.includeCapitalGrants = false;
          newState.includeOperationalSubsidies = false;
          newState.includeLoanSubsidies = true;
          newState.includeExportSupport = false;
          break;
        case 'export':
          newState.includeCapitalGrants = false;
          newState.includeOperationalSubsidies = false;
          newState.includeLoanSubsidies = false;
          newState.includeExportSupport = true;
          newState.isExporter = true;
          break;
        case 'crop':
          newState.farmType = 'crop';
          break;
        case 'livestock':
          newState.farmType = 'livestock';
          break;
        default:
          newState = { ...prev };
      }
      return newState;
    });

    setResults(null);
    setSchedule([]);
    setErrors({});
    setShowFullSchedule(false);

  }, [subToolId]);

  // Сохранение расчета в localStorage
  const saveCalculation = (name) => {
    if (!results) return;

    const newSavedCalculation = {
      id: Date.now().toString(),
      name: name || `Хозяйство ${formData.farmSize} га (${getFarmTypeName(formData.farmType)})`,
      date: new Date().toISOString(),
      formData: { ...formData },
      results: { ...results }
    };

    const updatedSavedCalculations = [...savedCalculations, newSavedCalculation];
    setSavedCalculations(updatedSavedCalculations);
    localStorage.setItem('savedAgriculturalSubsidies', JSON.stringify(updatedSavedCalculations));

    return newSavedCalculation;
  };

  // Удаление сохраненного расчета
  const deleteSavedCalculation = (id) => {
    const updatedSavedCalculations = savedCalculations.filter(calc => calc.id !== id);
    setSavedCalculations(updatedSavedCalculations);
    localStorage.setItem('savedAgriculturalSubsidies', JSON.stringify(updatedSavedCalculations));
  };

  // Загрузка сохраненного расчета
  const loadSavedCalculation = (id) => {
    const calculationToLoad = savedCalculations.find(calc => calc.id === id);
    if (calculationToLoad) {
      setFormData(calculationToLoad.formData);
      setResults(calculationToLoad.results);
      // Перерасчет графика для визуализации
      if (calculationToLoad.results) {
        calculateSubsidySchedule(calculationToLoad.formData, calculationToLoad.results);
      }
    }
  };

  // Вспомогательная функция для получения названия типа хозяйства
  const getFarmTypeName = (typeId) => {
    const farmType = farmTypes.find(type => type.id === typeId);
    return farmType ? farmType.name : 'Неизвестный тип';
  };

  // Функция расчета графика выплат субсидий
  const calculateSubsidySchedule = (data, subsidyResults) => {
    const scheduleDuration = parseInt(data.programDuration, 10) || 3;
    const startDate = new Date(data.startDate || new Date());
    
    const generatedSchedule = [];
    
    // Генерируем график для каждого года программы
    for (let year = 0; year < scheduleDuration; year++) {
      const currentYear = startDate.getFullYear() + year;
      const quarterlyAmount = subsidyResults.totalSubsidyAmount / (scheduleDuration * 4);
      
      // Добавляем записи для каждого квартала
      for (let quarter = 1; quarter <= 4; quarter++) {
        let quarterStartMonth = (quarter - 1) * 3;
        let paymentDate = new Date(currentYear, quarterStartMonth, 15);
        
        // Используем разные проценты для разных кварталов для реалистичности
        const quarterMultipliers = [1.1, 0.9, 0.8, 1.2]; // Q1, Q2, Q3, Q4
        const actualAmount = quarterlyAmount * quarterMultipliers[quarter - 1];
        
        // Формируем описание для разных типов субсидий
        let description = '';
        if (data.includeCapitalGrants && subsidyResults.capitalGrantAmount > 0) {
          const purpose = capitalGrantPurposes.find(p => p.id === data.capitalGrantsPurpose);
          description += `Грант на ${purpose ? purpose.name.toLowerCase() : 'капитальные затраты'} `;
        }
        
        if (data.includeOperationalSubsidies && subsidyResults.operationalSubsidyAmount > 0) {
          const type = operationalSubsidyTypes.find(t => t.id === data.operationalSubsidiesType);
          description += `Субсидия на ${type ? type.name.toLowerCase() : 'операционные расходы'} `;
        }
        
        if (data.includeLoanSubsidies && subsidyResults.loanSubsidyAmount > 0) {
          description += `Субсидирование процентной ставки по кредиту `;
        }
        
        if (data.includeExportSupport && subsidyResults.exportSupportAmount > 0) {
          description += `Поддержка экспортной деятельности `;
        }
        
        // Добавляем запись в график
        generatedSchedule.push({
          year: currentYear,
          quarter: quarter,
          date: paymentDate,
          amount: actualAmount,
          description: description.trim() || 'Выплата субсидии',
          paymentStatus: year === 0 && quarter === 1 ? 'approved' : (year === 0 && quarter < 3 ? 'processing' : 'planned')
        });
      }
    }
    
    setSchedule(generatedSchedule);
  };

  // --- RENDER ---
  return (
    <div className={`min-h-screen ${darkMode ? 'dark' : ''} bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-200 font-sans`}>
      {/* Background Pattern */}
      <div aria-hidden="true" className="fixed inset-0 bg-[radial-gradient(theme(colors.emerald.200)_1px,transparent_1px)] dark:bg-[radial-gradient(theme(colors.emerald.800)_1px,transparent_1px)] [background-size:24px_24px] opacity-20 dark:opacity-10 pointer-events-none z-[-1]"></div>

      <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode} />

      <main className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumbs />

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          {/* --- Main Content Area --- */}
          <div className="lg:w-2/3 flex-shrink-0">
            {/* Page Header */}
            <div className="mb-8">
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-3">
                <Tractor className="w-8 h-8 text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
                Калькулятор сельскохозяйственных субсидий
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                Рассчитайте доступные субсидии для вашего сельскохозяйственного бизнеса: гранты на развитие, компенсацию затрат, 
                льготное кредитование и другие меры государственной поддержки.
              </p>
            </div>

            {/* --- Форма калькулятора --- */}
            <CalculationForm 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              setErrors={setErrors}
              isCalculating={isCalculating}
              setIsCalculating={setIsCalculating}
              results={results}
              setResults={setResults}
              schedule={schedule}
              setSchedule={setSchedule}
              farmTypes={farmTypes}
              organizationTypes={organizationTypes}
              regions={regions}
              capitalGrantPurposes={capitalGrantPurposes}
              operationalSubsidyTypes={operationalSubsidyTypes}
              saveCalculation={saveCalculation}
              calculateSubsidySchedule={calculateSubsidySchedule}
            />

            {/* --- Результаты расчета --- */}
            {results && !isCalculating && (
              <>
                <ResultsOverview results={results} formData={formData} />
                <SubsidySchedule 
                  schedule={schedule} 
                  results={results}
                  showFullSchedule={showFullSchedule}
                  setShowFullSchedule={setShowFullSchedule}
                />
              </>
            )}

            {/* --- Информационный контент о субсидиях --- */}
            <InfoContent />
          </div>

          {/* --- Sidebar Area --- */}
          <aside className="lg:w-1/3 self-start space-y-8">
            {/* --- Панель быстрой настройки --- */}
            <QuickAdjustmentSidebar 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              isCalculating={isCalculating}
              farmTypes={farmTypes}
              regions={regions}
            />

            {/* --- Панель сравнения расчетов --- */}
            <ComparisonSidebar 
              savedCalculations={savedCalculations}
              deleteSavedCalculation={deleteSavedCalculation}
              loadSavedCalculation={loadSavedCalculation}
              farmTypes={farmTypes}
            />

            {/* --- Панель полезных советов --- */}
            <TipsSidebar />

            {/* --- Связанные инструменты --- */}
            {/* В реальном приложении здесь должен быть ToolSidebar */}
          </aside>
        </div>
      </main>

      <Footer />

      {/* --- Global Styles --- */}
      <style jsx global>{`
        /* Base Input Styles */
        .form-input, .form-select, .form-textarea {
          @apply block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 shadow-sm focus:border-emerald-500 focus:ring-emerald-500 sm:text-sm text-gray-900 dark:text-white transition duration-150 ease-in-out disabled:opacity-60 disabled:cursor-not-allowed dark:placeholder-gray-400;
        }
        .form-checkbox {
          @apply h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-emerald-600 focus:ring-emerald-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        .form-radio {
          @apply h-4 w-4 border-gray-300 dark:border-gray-600 text-emerald-600 focus:ring-emerald-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        /* Error State Styling */
        .input-error {
          @apply border-red-500 dark:border-red-500 text-red-900 dark:text-red-300 placeholder-red-400 dark:placeholder-red-600 focus:border-red-500 dark:focus:border-red-500 focus:ring-red-500 dark:focus:ring-red-500;
        }

        /* Custom Scrollbar for Subsidy Schedule Table */
        .subsidy-schedule-table {
          scrollbar-width: thin;
          scrollbar-color: theme('colors.gray.400') theme('colors.gray.100');
        }
        .dark .subsidy-schedule-table {
          scrollbar-color: theme('colors.gray.600') theme('colors.gray.800');
        }
        .subsidy-schedule-table::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        .subsidy-schedule-table::-webkit-scrollbar-track {
          background-color: theme('colors.gray.100');
          border-radius: 10px;
        }
        .dark .subsidy-schedule-table::-webkit-scrollbar-track {
          background-color: theme('colors.gray.800');
        }
        .subsidy-schedule-table::-webkit-scrollbar-thumb {
          background-color: theme('colors.gray.400');
          border-radius: 10px;
          border: 2px solid theme('colors.gray.100');
        }
        .dark .subsidy-schedule-table::-webkit-scrollbar-thumb {
          background-color: theme('colors.gray.600');
          border-color: theme('colors.gray.800');
        }
        .subsidy-schedule-table::-webkit-scrollbar-thumb:hover {
          background-color: theme('colors.gray.500');
        }
        .dark .subsidy-schedule-table::-webkit-scrollbar-thumb:hover {
          background-color: theme('colors.gray.500');
        }
      `}</style>
    </div>
  );
};

export default AgriculturalSubsidiesCalculatorPage;